import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import SeoHead from "../components/SeoHead";
import { Search, Clock, ArrowUpRight } from "lucide-react";
import HrefLangTags from "../components/HrefLangTags";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import BlogCategoryChips from "../components/BlogCategoryChips";
import { useLanguage } from "../contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category translation map (shared with BlogCategoryChips)
const CAT_TRANSLATIONS = {
  "Performans": { en: "Performance", es: "Rendimiento", de: "Performance", fr: "Performance", ar: "الأداء" },
  "SEO": { en: "SEO", es: "SEO", de: "SEO", fr: "SEO", ar: "سيو" },
  "Sosyal Medya": { en: "Social Media", es: "Redes Sociales", de: "Social Media", fr: "Réseaux Sociaux", ar: "التواصل الاجتماعي" },
  "Kreatif": { en: "Creative", es: "Creativo", de: "Kreativ", fr: "Créatif", ar: "إبداعي" },
  "Strateji": { en: "Strategy", es: "Estrategia", de: "Strategie", fr: "Stratégie", ar: "الاستراتيجية" },
  "Marka": { en: "Brand", es: "Marca", de: "Marke", fr: "Marque", ar: "العلامة التجارية" },
  "Analitik": { en: "Analytics", es: "Analítica", de: "Analytik", fr: "Analytique", ar: "التحليلات" },
  "E-Ticaret": { en: "E-Commerce", es: "E-Commerce", de: "E-Commerce", fr: "E-Commerce", ar: "التجارة الإلكترونية" },
  "Yapay Zeka": { en: "AI & Automation", es: "IA & Automatización", de: "KI & Automatisierung", fr: "IA & Automatisation", ar: "الذكاء الاصطناعي" },
};
function translateCategory(cat, lang) {
  if (lang === "tr") return cat;
  return CAT_TRANSLATIONS[cat]?.[lang] || cat;
}

function ArticleCard({ a, lang, featured }) {
  const tr = a.translations || {};
  return (
    <Link
      to={`/${lang}/blog/${a.slug}`}
      data-testid={`blog-card-${a.slug}`}
      className={`group block rounded-3xl bg-white border border-mist overflow-hidden
                  transition-[transform,border-color,box-shadow] duration-500 ease-out-quart
                  hover:-translate-y-1 hover:border-brand-pink/60 hover:shadow-card
                  ${featured ? "lg:col-span-2 lg:row-span-2" : ""}`}
    >
      <div className={`relative overflow-hidden bg-mist ${featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
        {a.cover_image_url ? (
          <img src={a.cover_image_url} alt={tr.title} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out-quart group-hover:scale-105" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-brand-pink/20 via-brand-magenta/15 to-brand-teal/20" />
        )}
        <div className="absolute inset-x-4 top-4 flex items-center justify-between">
          <span className="rounded-full bg-cream/90 backdrop-blur-sm text-ink text-[0.65rem] uppercase tracking-[0.18em] font-bold px-2.5 py-1">
            {translateCategory(a.category, lang)}
          </span>
        </div>
      </div>
      <div className="p-5 sm:p-6">
        <h3 className={`font-display font-bold text-ink leading-snug ${featured ? "text-h1" : "text-h2"} line-clamp-2`}>
          {tr.title}
        </h3>
        <p className="mt-3 text-sm text-ink/65 line-clamp-3">{tr.excerpt}</p>
        <div className="mt-5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-ink/50">
            <Clock className="h-3.5 w-3.5" />
            {a.reading_time_min || 8} dk · {a.published_at ? new Date(a.published_at).toLocaleDateString(lang === "ar" ? "ar-EG" : `${lang}-${lang.toUpperCase()}`) : ""}
          </span>
          <ArrowUpRight className="h-4 w-4 text-ink/40 group-hover:text-brand-pink transition-colors" />
        </div>
      </div>
    </Link>
  );
}

export default function BlogIndexPage() {
  const { t, lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fetchArticles = (attempt = 0) => {
      setLoading(true);
      axios.get(`${API}/blog/articles`, { params: { lang, limit: 50, category: activeCategory } })
        .then((r) => {
          if (cancelled) return;
          const data = r.data?.articles || r.data || [];
          if (data.length === 0 && attempt < 3) {
            // Empty — server may still be seeding, retry
            retryTimer = setTimeout(() => fetchArticles(attempt + 1), 2500);
          } else {
            setArticles(data);
            setLoading(false);
          }
        })
        .catch(() => { if (!cancelled) { setArticles([]); setLoading(false); } });
    };

    fetchArticles();
    axios.get(`${API}/blog/categories`).then((r) => { if (!cancelled) setCategories(r.data || []); }).catch(() => {});
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [lang, activeCategory]);

  const filtered = useMemo(() => {
    if (!search.trim()) return articles;
    const q = search.toLowerCase();
    return articles.filter((a) => {
      const tr = a.translations || {};
      return (tr.title || "").toLowerCase().includes(q) ||
             (tr.excerpt || "").toLowerCase().includes(q);
    });
  }, [articles, search]);

  const [featured, ...rest] = filtered;
  const isTR = lang === "tr";
  const pageTitle = `Blog · Yastık Medya`;
  const blogTitle = isTR ? "Dijital pazarlama" : lang === "en" ? "Digital marketing" : lang === "es" ? "Marketing digital" : lang === "de" ? "Digitales Marketing" : lang === "fr" ? "Marketing numérique" : "التسويق الرقمي";
  const blogAccent = isTR ? "bilgeliği." : lang === "en" ? "wisdom." : lang === "es" ? "sabiduría." : lang === "de" ? "Wissen." : lang === "fr" ? "sagesse." : "حكمة.";
  const blogSubtitle = isTR
    ? "Performans, SEO, sosyal medya, kreatif strateji ve marka inşasına dair Yastık Medya'nın saha notları."
    : lang === "en" ? "Yastık Medya's field notes on performance, SEO, social media, creative strategy and brand building."
    : lang === "es" ? "Las notas de campo de Yastık Medya sobre rendimiento, SEO, redes sociales, estrategia creativa y construcción de marca."
    : lang === "de" ? "Yastık Medyas Feldnotizen zu Performance, SEO, Social Media, kreativer Strategie und Markenaufbau."
    : lang === "fr" ? "Les notes de terrain de Yastık Medya sur la performance, le SEO, les réseaux sociaux, la stratégie créative et la construction de marque."
    : "ملاحظات ميدانية من يسطق ميديا حول الأداء والسيو ووسائل التواصل الاجتماعي والاستراتيجية الإبداعية وبناء العلامة التجارية.";
  const searchPlaceholder = isTR ? "Yazıları ara…" : lang === "en" ? "Search articles…" : lang === "es" ? "Buscar artículos…" : lang === "de" ? "Artikel suchen…" : lang === "fr" ? "Rechercher des articles…" : "البحث في المقالات…";
  const loadingText = isTR ? "Yükleniyor…" : "Loading…";
  const notFoundText = isTR ? "Yazı bulunamadı." : "No articles found.";

  return (
    <div data-testid="blog-index-page" className="min-h-screen bg-cream text-ink">
      <SeoHead title={pageTitle} description="Yastık Medya blog — dijital pazarlama, performans, SEO ve kreatif strateji üzerine uzman rehberler." path={`/${lang}/blog`} imageKey="blog" lang={lang} />
      <HrefLangTags pathSuffix="blog" />
      <Header />

      <main>
        <section className="py-section sm:py-section-lg">
          <div className="container-page">
            <Reveal as="div" className="max-w-3xl">
              <span className="eyebrow"><span className="h-1 w-1 rounded-full bg-brand-pink" />BLOG</span>
              <h1 className="mt-5 font-display text-display-1 font-bold leading-[0.98] text-ink">
                {blogTitle}{" "}
                <span className="gold-foil-text">{blogAccent}</span>
              </h1>
              <p className="mt-6 text-body-lg text-ink/65 leading-relaxed max-w-xl">
                {blogSubtitle}
              </p>
            </Reveal>

            <div className="mt-12 flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute start-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="blog-search"
                  placeholder={searchPlaceholder}
                  className="w-full rounded-full border-2 border-mist bg-white ps-10 pe-4 py-3 text-sm focus:border-brand-pink outline-none transition-colors"
                />
              </div>
            </div>

            <div className="mt-6">
              <BlogCategoryChips
                active={activeCategory}
                onChange={setActiveCategory}
                dynamicCategories={categories}
              />
            </div>

            <div className="mt-12">
              {loading ? (
                <div className="text-center py-20 text-ink/55">{loadingText}</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-ink/55">{notFoundText}</div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {featured ? <ArticleCard key={featured.id || featured.slug} a={featured} lang={lang} featured /> : null}
                  {rest.map((a, i) => <ArticleCard key={a.id || `${a.slug}-${i}`} a={a} lang={lang} />)}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
